const STATE_KEY = "chuzi.oidc.state";
const VERIFIER_KEY = "chuzi.oidc.codeVerifier";

function requiredEnv(name: string): string {
  const value = (import.meta.env[name] as string | undefined)?.trim();
  if (!value) {
    throw new Error(`Missing environment variable: ${name}`);
  }
  if (value.toLowerCase().includes("placeholder")) {
    throw new Error(
      "Authentication is not configured for this environment yet. Please deploy with real Cognito values.",
    );
  }
  return value;
}

function randomString(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => (b % 36).toString(36)).join("");
}

async function sha256Base64Url(value: string): Promise<string> {
  const encoded = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", encoded);
  const bytes = new Uint8Array(hash);
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function beginOidcLogin(): Promise<void> {
  const authBaseUrl = requiredEnv("VITE_COGNITO_AUTH_BASE_URL");
  const clientId = requiredEnv("VITE_COGNITO_CLIENT_ID");
  const redirectUri = requiredEnv("VITE_COGNITO_REDIRECT_URI");

  const codeVerifier = randomString(96);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomString(48);

  localStorage.setItem(STATE_KEY, state);
  localStorage.setItem(VERIFIER_KEY, codeVerifier);

  const url = new URL(`${authBaseUrl.replace(/\/+$/, "")}/oauth2/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("identity_provider", "Google");
  url.searchParams.set("prompt", "select_account");
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");

  window.location.assign(url.toString());
}

/** Clear the Cognito (and federated IdP) session so the next sign-in can pick an account. */
export function beginOidcLogout(): void {
  try {
    const authBaseUrl = requiredEnv("VITE_COGNITO_AUTH_BASE_URL");
    const clientId = requiredEnv("VITE_COGNITO_CLIENT_ID");
    const logoutUri = `${window.location.origin}/login`;

    const url = new URL(`${authBaseUrl.replace(/\/+$/, "")}/logout`);
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("logout_uri", logoutUri);

    window.location.assign(url.toString());
  } catch {
    window.location.assign("/login");
  }
}

export function consumeOidcVerifier(expectedState: string): string {
  const storedState = localStorage.getItem(STATE_KEY);
  const verifier = localStorage.getItem(VERIFIER_KEY);

  localStorage.removeItem(STATE_KEY);
  localStorage.removeItem(VERIFIER_KEY);

  if (!storedState || storedState !== expectedState || !verifier) {
    throw new Error("Could not validate sign-in state.");
  }
  return verifier;
}

export function oidcRedirectUri(): string {
  return requiredEnv("VITE_COGNITO_REDIRECT_URI");
}
